import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { orgPropAPI } from '../utils/apiClient';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export const UserProvider = ({ children, authData, selectedOrg }) => {
  const auth = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      const token = auth.user?.access_token;
      const sub = auth.user?.profile?.sub;

      if (!token || !sub) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // For superadmins, use the selected org from AuthWrapper
        if (authData?.isSuperadmin && selectedOrg) {
          setUserData({
            o: selectedOrg.hash,
            r: 'superadmin',
            isSuperadmin: true,
          });
        } else {
          // For regular users/admins, fetch from API
          const response = await orgPropAPI.getOrgProp(token, sub);
          console.log('User details:', response);

          // Add expiration timestamp (30 days)
          const userDataWithExpiry = {
            ...response,
            expiresAt: new Date().getTime() + 30 * 24 * 60 * 60 * 1000
          };

          // Save to localStorage
          localStorage.setItem('LLUP', JSON.stringify(userDataWithExpiry));

          setUserData({
            o: response.o,
            r: response.r,
            isSuperadmin: false,
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (auth.isAuthenticated) {
      fetchUserData();
    }
  }, [auth.isAuthenticated, auth.user, authData?.isSuperadmin, selectedOrg]);

  const contextValue = {
    userData,
    loading,
    setUserData,
    // Include permission info
    role: authData?.role,
    isSuperadmin: authData?.isSuperadmin || false,
    isAdmin: authData?.role === 'admin',
    isUser: authData?.role === 'user',
    orgHash: selectedOrg?.hash || userData?.o,
    orgName: selectedOrg?.name,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};